/**
 * Reading an API response body without assuming what format it is in — and,
 * either side of that, making sure the two ways an exchange can fail *outside*
 * the status code still reach the user as a sentence.
 *
 * The api-server's own controllers answer in one shape: auth_ctrl.go and
 * mfa_ctrl.go return a FistoneApiError envelope for every error they raise.
 * That does not make the tolerance below redundant, because the API is not the
 * only thing answering on these paths. Echo's DefaultHTTPErrorHandler keeps a
 * shape of its own for 404/405 and panics — `{"message":...}` with no `error`
 * key, framework copy rather than product copy — and a proxy in front of the
 * app is under no obligation to answer in anyone's envelope, an HTML error
 * page included. One non-envelope JSON body also survives inside the API by
 * design: /login's 428 challenge, which is a second step rather than a failure.
 *
 * `response.json()` does not consult Content-Type — it reads the body to text
 * and JSON.parses it regardless — so calling it on a text/plain or empty body
 * throws. Reading as text and parsing locally sidesteps that entirely: the
 * body is read once, and attempting the parse *is* the content-type check,
 * which is authoritative in a way the header is not. A proxy can rewrite a
 * header; it cannot make a body parse that would not otherwise.
 *
 * The two failures that never carry a status code at all are handled by
 * `sendRequest` and `readSuccessBody` below. Both exist for the same reason
 * postLogin.js:86-92 has its catch: every caller in this codebase ends in
 * `setError(err.message)`, so an unhandled rejection from the transport is not
 * a crash — it is a browser-authored string rendered inside an MUI Alert.
 */

// A plain-text error body longer than this reads as something other than the
// API's own short, hand-written message — a proxy's HTML error page, for
// instance — so it is not fit to show as-is.
export const MAX_ERROR_MESSAGE_LENGTH = 300;

/**
 * Shown when the request never reached the server, or its answer never reached
 * us. Word-for-word what Login.js, MfaCodeStep.js, Register.js and
 * ForgotPassword.js have always rendered from their own catch blocks, so the
 * services that now produce it read the same as the pages that do not.
 */
export const NETWORK_ERROR_MESSAGE = 'Network error. Please try again.';

/**
 * Shown when the server answered 2xx with something that is not the JSON the
 * endpoint promises. Deliberately vaguer than the network sentence: from the
 * user's side this is not "you are offline", it is "the answer was gibberish",
 * and there is nothing more specific worth saying about that.
 */
export const UNREADABLE_RESPONSE_MESSAGE = 'Something went wrong. Please try again.';

/**
 * `fetch`, with a transport failure turned into a sentence a person can read.
 *
 * `fetch` rejects — rather than resolving with a status — for everything that
 * happens before there is an HTTP response at all: offline, DNS failure,
 * connection reset, a TLS error, a blocked request. The rejection carries the
 * browser's own wording, and "Failed to fetch" (Chrome) or "NetworkError when
 * attempting to fetch resource" (Firefox) is not copy anyone chose to show.
 *
 * Only the transport is wrapped. Status mapping stays outside, in each
 * service's own `raise`, so the typed errors this codebase dispatches on
 * (InvalidCodeError, TooManyAttemptsError, SessionExpiredError, ...) are never
 * inside this try and can never be flattened into a generic Error by it.
 */
export const sendRequest = async (url, options) => {
  try {
    return await fetch(url, options);
  } catch {
    throw new Error(NETWORK_ERROR_MESSAGE);
  }
};

/**
 * Parse the JSON body of a response already known to be OK.
 *
 * The counterpart to `readBody`, which is for *error* bodies, where the format
 * is genuinely mixed. A 2xx from these endpoints is always a JSON object, so
 * there is nothing to sniff and `response.json()` is right — but "always" is a
 * statement about the API, and a captive-portal login page or a misconfigured
 * proxy can put an HTML 200 in front of it. Unwrapped, that reaches the user as
 * `Unexpected token '<', "<html>..." is not valid JSON`.
 *
 * A body that parses but is not an object gets the same treatment, for
 * readBody's reason: `'null'`, `'123'` and `'[1,2]'` all parse, and none of
 * them is a shape a caller here can read a field off. Letting one through only
 * moves the failure to the first property access, where it arrives as a
 * TypeError with no idea left of what went wrong.
 */
export const readSuccessBody = async (response) => {
  let parsed;
  try {
    parsed = await response.json();
  } catch {
    throw new Error(UNREADABLE_RESPONSE_MESSAGE);
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(UNREADABLE_RESPONSE_MESSAGE);
  }

  return parsed;
};

/**
 * Read a response body exactly once, whatever format it is in. Never throws.
 *
 * Consumes the stream, so nothing may call `response.json()` or
 * `response.text()` afterwards. Safe on a 204: `text()` resolves to '',
 * `JSON.parse('')` throws, and the result is `{text: '', json: null}`.
 *
 * @param {Response} response
 * @returns {Promise<{text: string, json: object|null, code: string|null}>}
 */
export const readBody = async (response) => {
  const text = (await response.text().catch(() => '')).trim();

  let json = null;
  try {
    const parsed = JSON.parse(text);
    // JSON.parse succeeds on non-objects too — '123', 'null', '"hi"', '[1,2]'
    // all parse. Only an object is a body shape any caller here can use, and
    // letting a truthy non-object through would break every `if (body.json)`.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) json = parsed;
  } catch {
    /* a text/plain body — expected here, not exceptional */
  }

  return {
    text,
    json,
    // Two namespaces share this field: `mfa_required` (models.MFAChallengeResponse,
    // lowercase, and not a FistoneApiError at all) and the SCREAMING_SNAKE codes
    // from apierrors.FistoneApiError. Callers must branch on (status, code),
    // never on code alone — not because the API is inconsistent, but because
    // one endpoint deliberately kept its lowercase challenge when the rest of
    // the surface moved to the envelope. The type guard matters because the body is untyped
    // JSON: a non-string `error` would otherwise arrive truthy in a caller's
    // dispatch, and `error: ""` would arrive as '' rather than null.
    code: typeof json?.error === 'string' && json.error ? json.error : null,
  };
};

/**
 * The message from a body that is fit to show a user, or `fallback`.
 *
 * A `message` is only display-suitable when it arrives inside the
 * `{error, message}` envelope that promises it is (apierrors.FistoneApiError,
 * fistone_api_error.go:12-15). Echo's DefaultHTTPErrorHandler answers
 * `{"message":"Not Found"}` with no `error` key — framework copy, not product
 * copy, and not ours to put in front of anyone. So the presence of a `code` is
 * what licenses reading `message`.
 *
 * `text` is likewise never a fallback for a JSON body, because on one it holds
 * the raw source string: without that guard, `{"error":"INVALID_CODE"}` with no
 * message renders as literal JSON in an alert.
 *
 * Rule: JSON -> envelope messages only; not JSON -> the plain text; otherwise
 * the caller's fallback.
 *
 * @param {{text: string, json: object|null, code: string|null}} body
 * @param {string} fallback
 * @returns {string}
 */
export const messageFrom = ({ text, json, code }, fallback) => {
  // `json?.message` rather than `json.message`: `code` implies a non-null
  // `json` only for a body that came from readBody, and test helpers construct
  // these by hand.
  const candidate = code
    ? (typeof json?.message === 'string' ? json.message : '')
    : (json ? '' : text);

  return candidate && candidate.length <= MAX_ERROR_MESSAGE_LENGTH ? candidate : fallback;
};
